import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import { BLOG_POSTS, BLOG_CATEGORIES, formatDate } from "../data/blog";
import { Seo } from "../components/Seo";
import { Reveal, LineReveal } from "../components/Reveal";

const ease = [0.22, 1, 0.36, 1];

const PostCard = ({ post, i }) => (
  <motion.article
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-8% 0px" }}
    transition={{ duration: 0.9, delay: (i % 3) * 0.1, ease }}
    className="group flex flex-col border border-[var(--line)] bg-[var(--bg-2)] hover:border-[var(--line-strong)] transition-colors duration-500"
    data-testid={`blog-card-${post.slug}`}
  >
    <Link to={`/blog/${post.slug}`} className="frame aspect-[3/2] block" aria-label={post.title}>
      <img src={post.image} alt={post.imageAlt} className="w-full h-full object-cover" loading="lazy" />
      <div className="veil" />
      <span className="absolute top-4 left-4 bg-[rgba(10,10,11,0.9)] text-[var(--gold)] font-mono text-[0.6rem] tracking-[0.25em] uppercase px-2.5 py-1.5">{post.category}</span>
    </Link>
    <div className="p-7 flex flex-col flex-1">
      <div className="flex items-center gap-4 font-mono text-[0.6rem] tracking-[0.2em] uppercase text-[var(--muted-2)]">
        <time dateTime={post.date}>{formatDate(post.date)}</time>
        <span className="flex items-center gap-1.5"><Clock size={11} /> {post.readTime} Min.</span>
      </div>
      <h2 className="font-display text-2xl leading-snug mt-4 text-[var(--text)] group-hover:text-[var(--gold-3)] transition-colors">
        <Link to={`/blog/${post.slug}`}>{post.title}</Link>
      </h2>
      <p className="mt-4 text-sm text-[var(--muted)] leading-relaxed flex-1">{post.excerpt}</p>
      <Link to={`/blog/${post.slug}`} className="mt-6 inline-flex items-center gap-2 text-xs font-semibold tracking-[0.15em] uppercase text-[var(--gold)]" data-testid={`blog-card-link-${post.slug}`}>
        Weiterlesen <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </Link>
    </div>
  </motion.article>
);

export default function Blog() {
  const [cat, setCat] = useState("Alle");
  const [featured, ...rest] = BLOG_POSTS;
  const filtered = BLOG_POSTS.filter((p) => cat === "Alle" || p.category === cat);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Salgin Solutions Ratgeber",
    description: "Ratgeber zu Amazon Produktbildern, KI-Produktvisualisierung, Lifestyle-Bildern, technischen Grafiken und A+ Content.",
    inLanguage: "de-DE",
    blogPost: BLOG_POSTS.map((p) => ({ "@type": "BlogPosting", headline: p.title, datePublished: p.date, author: { "@type": "Person", name: "Mustafa Salgin" } })),
  };

  return (
    <main className="pt-[76px]" data-testid="blog-page">
      <Seo
        title="Ratgeber: Produktbilder, Amazon & KI-Visualisierung | Salgin Solutions"
        description="Praxiswissen für Onlinehändler: Amazon Produktbilder optimieren, KI-Produktbilder vs. Fotoshooting, Lifestyle-Bilder, technische Grafiken und A+ Content – vom Experten für KI-Produktvisualisierung."
        path="/blog"
        jsonLd={jsonLd}
      />

      <section className="relative py-20 lg:py-28 spotlight">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Reveal y={10}><p className="eyebrow mb-6">Ratgeber & Blog</p></Reveal>
            <LineReveal as="h1" lines={["Wissen, das", "Produkte verkauft."]} className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.02] tracking-tight" />
            <Reveal delay={0.3} y={16}>
              <p className="mt-8 text-base md:text-lg text-[var(--muted)] leading-relaxed max-w-xl">
                Praxiswissen für Onlinehändler und Marken: Amazon-Bildrichtlinien, KI-Produktvisualisierung, Lifestyle-Inszenierung, technische Grafiken und A+ Content – kompakt, verständlich und sofort anwendbar.
              </p>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-5 lg:justify-self-end" delay={0.2}>
            <div className="flex flex-wrap gap-2" data-testid="blog-categories">
              {BLOG_CATEGORIES.map((c) => (
                <button key={c} onClick={() => setCat(c)} className={`px-4 py-2 border text-[0.7rem] font-mono tracking-[0.15em] uppercase transition-colors ${cat === c ? "border-[var(--gold)] bg-[var(--gold)] text-[#0a0a0b]" : "border-[var(--line)] text-[var(--muted)] hover:border-[var(--line-strong)] hover:text-[var(--text)]"}`} data-testid={`blog-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {c}
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {cat === "Alle" && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-6">
          <motion.article
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease }}
            className="group grid lg:grid-cols-12 border border-[var(--line-strong)] bg-[var(--bg-2)] overflow-hidden"
            data-testid="blog-featured"
          >
            <Link to={`/blog/${featured.slug}`} className="lg:col-span-7 frame frame-corner aspect-[3/2] lg:aspect-auto lg:min-h-[460px] block" aria-label={featured.title}>
              <img src={featured.image} alt={featured.imageAlt} className="absolute inset-0 w-full h-full object-cover" />
              <div className="veil" />
            </Link>
            <div className="lg:col-span-5 p-8 lg:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-4 font-mono text-[0.6rem] tracking-[0.2em] uppercase text-[var(--gold)]">
                <span>Empfohlen</span><span className="w-8 h-px bg-[var(--line-strong)]" /><span className="text-[var(--muted-2)]">{featured.category}</span>
              </div>
              <h2 className="font-display text-3xl lg:text-4xl leading-tight mt-6 group-hover:text-[var(--gold-3)] transition-colors">
                <Link to={`/blog/${featured.slug}`}>{featured.title}</Link>
              </h2>
              <p className="mt-5 text-[var(--muted)] leading-relaxed">{featured.excerpt}</p>
              <div className="mt-8 flex items-center justify-between">
                <span className="font-mono text-[0.6rem] tracking-[0.2em] uppercase text-[var(--muted-2)]">{formatDate(featured.date)} · {featured.readTime} Min. Lesezeit</span>
                <Link to={`/blog/${featured.slug}`} className="btn-gold !py-3 !px-5 text-xs" data-testid="blog-featured-link">Lesen <ArrowUpRight size={14} /></Link>
              </div>
            </div>
          </motion.article>
        </section>
      )}

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="blog-grid">
          {(cat === "Alle" ? rest : filtered).map((p, i) => <PostCard key={p.slug} post={p} i={i} />)}
        </div>
      </section>

      <section className="border-t border-[var(--line)] bg-[var(--bg-2)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <p className="eyebrow mb-4">Vom Wissen zur Umsetzung</p>
            <h2 className="font-display text-3xl lg:text-4xl font-light leading-tight">Sie möchten Ihre Produktbilder professionell erstellen lassen?</h2>
            <p className="mt-4 text-[var(--muted)] max-w-xl">Salgin Solutions erstellt Amazon-Listing-Bilder, Lifestyle-Szenen, technische Grafiken und A+ Content – individuell, schnell und ab 7,50 € pro Bild.</p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/" state={{ scrollTo: "preise" }} className="btn-gold" data-testid="blog-cta-pricing">Preise ansehen <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>
    </main>
  );
}
